"use client";

import { RotateCcwIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useSendAiPlannerMessageMutation } from "@/feature/ai-planner/api/ai-chat-api";
import { Button } from "@/components/ui/button";
import {
  appendAiPlannerTurn,
  disableAiPlannerMessageAnimation,
  resetAiPlannerSession,
  resolveAiPlannerTurnResponse,
  selectAiPlannerState,
  setAiPlannerDraft,
  setAiPlannerSuggestedQueries,
} from "@/feature/ai-planner/state/ai-planner-slice";
import type {
  AiChatAction,
  AiSuggestedQuery,
} from "@/feature/ai-planner/types/ai-chat.types";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  buildAssistantMessage,
  buildFlightRedirectPath,
  buildHotelRedirectPath,
  createClientId,
  getActionFallbackMessage,
} from "../lib/ai-planner-chat.helpers";
import { AiPlannerStarterView } from "./ai-planner-starter-view";
import { ChatComposer } from "./chat-composer";
import { ChatTurnItem } from "./chat-turn-item";
import type { ChatMessage, ChatTurn } from "../types/chat-ui.types";
import { SuggestedQueryChips } from "./suggested-query-chips";

type DeferredSuggestedQueries = {
  messageId: string;
  queries: AiSuggestedQuery[];
};

export function AiPlannerChat() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { turns, sessionId, draft, activeSuggestedQueries } =
    useAppSelector(selectAiPlannerState);
  const [_deferredSuggestedQueries, setDeferredSuggestedQueries] =
    useState<DeferredSuggestedQueries | null>(null);
  const [pendingSuggestedQueryId, setPendingSuggestedQueryId] = useState<
    string | null
  >(null);
  const [lastTurnMinHeight, setLastTurnMinHeight] = useState(320);
  const [stickyBottomSpace, setStickyBottomSpace] = useState(220);
  const [sendAiMessage, { isLoading: isSending }] =
    useSendAiPlannerMessageMutation();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const composerDockRef = useRef<HTMLDivElement | null>(null);
  const scrollTargetRef = useRef<string | null>(null);

  /* ── layout metrics (ResizeObserver) ── */
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const measure = () => {
      const dockH =
        composerDockRef.current?.getBoundingClientRect().height ?? 0;
      const bottom = Math.max(160, Math.round(dockH + 24));
      const minH = Math.max(
        360,
        Math.round(container.clientHeight - bottom - 24),
      );
      setStickyBottomSpace(bottom);
      setLastTurnMinHeight(minH);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    if (composerDockRef.current) ro.observe(composerDockRef.current);
    return () => ro.disconnect();
  }, []);

  /* ── auto-scroll to newest turn ── */
  useLayoutEffect(() => {
    const id = scrollTargetRef.current;
    const container = scrollContainerRef.current;
    if (!id || !container) return;
    scrollTargetRef.current = null;

    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const node = container.querySelector<HTMLElement>(
          `[data-turn-id="${id}"]`,
        );
        if (!node) return;
        const offset =
          container.scrollTop +
          node.getBoundingClientRect().top -
          container.getBoundingClientRect().top;
        container.scrollTo({ top: Math.max(0, offset), behavior: "smooth" });
      }),
    );
    return () => cancelAnimationFrame(frame);
  }, [turns.length, lastTurnMinHeight]);

  const sendMessage = useCallback(
    async (
      rawText: string,
      options?: { pendingSuggestedQueryId?: string | null },
    ) => {
      const text = rawText.trim();
      if (!text || isSending) return;

      const turnId = createClientId("turn");
      const userMessage: ChatMessage = {
        id: createClientId("user"),
        role: "user",
        text,
        animateText: false,
        createdAt: Date.now(),
      };

      scrollTargetRef.current = turnId;
      dispatch(appendAiPlannerTurn({ id: turnId, request: userMessage }));
      dispatch(setAiPlannerDraft(""));
      dispatch(setAiPlannerSuggestedQueries([]));
      setDeferredSuggestedQueries(null);
      setPendingSuggestedQueryId(options?.pendingSuggestedQueryId ?? null);

      try {
        const payload = await sendAiMessage({
          sessionId,
          message: text,
        }).unwrap();

        const assistantMessage = buildAssistantMessage(payload);
        const nextSuggestedQueries = assistantMessage.suggestedQueries ?? [];

        if (nextSuggestedQueries.length) {
          if (assistantMessage.text) {
            setDeferredSuggestedQueries({
              messageId: assistantMessage.id,
              queries: nextSuggestedQueries,
            });
          } else {
            dispatch(setAiPlannerSuggestedQueries(nextSuggestedQueries));
          }
        }

        dispatch(
          resolveAiPlannerTurnResponse({
            turnId,
            response: assistantMessage,
          }),
        );
      } catch (error) {
        const fallbackMessage =
          typeof error === "string"
            ? error
            : error instanceof Error
              ? error.message
              : "Could not process this request. Please try again.";

        const errorMessage: ChatMessage = {
          id: createClientId("assistant-error"),
          role: "assistant",
          text: fallbackMessage,
          animateText: false,
          type: "ERROR",
          createdAt: Date.now(),
        };

        dispatch(
          resolveAiPlannerTurnResponse({
            turnId,
            response: errorMessage,
          }),
        );
      } finally {
        setPendingSuggestedQueryId(null);
      }
    },
    [dispatch, isSending, sendAiMessage, sessionId],
  );

  const handleMessageAction = useCallback(
    (action: AiChatAction) => {
      switch (action.type) {
        case "SEE_MORE_FLIGHTS": {
          const redirectPath = buildFlightRedirectPath(action);
          if (redirectPath) {
            router.push(redirectPath);
            return;
          }
          break;
        }
        case "SEE_MORE_HOTELS": {
          const redirectPath = buildHotelRedirectPath(action);
          if (redirectPath) {
            router.push(redirectPath);
            return;
          }
          break;
        }
        default:
          break;
      }

      void sendMessage(getActionFallbackMessage(action));
    },
    [router, sendMessage],
  );

  const handleResponseStreamingComplete = useCallback(
    (message: ChatTurn["response"]) => {
      if (!message) {
        return;
      }

      dispatch(
        disableAiPlannerMessageAnimation({
          messageId: message.id,
        }),
      );

      let nextQueries: AiSuggestedQuery[] | null = null;

      setDeferredSuggestedQueries((previous) => {
        if (!previous || previous.messageId !== message.id) {
          return previous;
        }

        nextQueries = previous.queries;
        return null;
      });

      if (nextQueries) {
        dispatch(setAiPlannerSuggestedQueries(nextQueries));
      }
    },
    [dispatch],
  );

  const handleSuggestedQuery = useCallback(
    (query: AiSuggestedQuery) => {
      void sendMessage(query.query, {
        pendingSuggestedQueryId: query.id,
      });
    },
    [sendMessage],
  );

  const handleClearSession = useCallback(() => {
    dispatch(resetAiPlannerSession());
    setDeferredSuggestedQueries(null);
    setPendingSuggestedQueryId(null);
    scrollTargetRef.current = null;
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [dispatch]);

  const showStarterView = useMemo(
    () => !turns.length && !isSending,
    [turns.length, isSending],
  );

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <div
        data-scroll-root
        ref={scrollContainerRef}
        className="relative z-10 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
        style={{ scrollbarGutter: "stable both-edges" }}
      >
        {showStarterView ? (
          <AiPlannerStarterView
            onSendPrompt={(prompt) => {
              void sendMessage(prompt);
            }}
          />
        ) : (
          <main className="mx-auto flex min-h-full w-full max-w-3xl min-w-0 flex-col px-3 sm:px-6">
            <div
              className={cn("flex w-full min-w-0 flex-col pt-6")}
              style={
                showStarterView
                  ? undefined
                  : { paddingBottom: stickyBottomSpace }
              }
            >
              <div className="w-full min-w-0 space-y-7 text-sm">
                {turns.map((turn, index) => (
                  <ChatTurnItem
                    key={turn.id}
                    turn={turn}
                    onAction={handleMessageAction}
                    onSuggestedQuery={handleSuggestedQuery}
                    onResponseStreamingComplete={
                      handleResponseStreamingComplete
                    }
                    showSuggestions={
                      index === turns.length - 1 && !pendingSuggestedQueryId
                    }
                    actionsDisabled={isSending}
                    pendingSuggestedQueryId={pendingSuggestedQueryId}
                    isLast={index === turns.length - 1}
                    minHeight={lastTurnMinHeight}
                    isPendingResponse={
                      isSending &&
                      turn === turns[turns.length - 1] &&
                      !turn.response
                    }
                  />
                ))}
              </div>
            </div>
          </main>
        )}
      </div>

      <div
        ref={composerDockRef}
        id="thread-bottom-container"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 overflow-x-hidden"
      >
        <div className="h-24 bg-linear-to-t from-background via-background to-transparent" />
        <div className="pointer-events-auto bg-background pb-4 backdrop-blur supports-backdrop-filter:bg-background">
          <div className="mx-auto w-full max-w-3xl px-3 sm:px-6">
            {!showStarterView ? (
              <div className="mb-2 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  disabled={isSending}
                  onClick={handleClearSession}
                >
                  <RotateCcwIcon className="size-3.5" />
                  Clear session
                </Button>
              </div>
            ) : null}
            <SuggestedQueryChips
              queries={!pendingSuggestedQueryId ? activeSuggestedQueries : []}
              onSelect={handleSuggestedQuery}
              disabled={isSending}
              pendingQueryId={pendingSuggestedQueryId}
            />
            <ChatComposer
              value={draft}
              isSending={isSending}
              onValueChange={(value) => {
                dispatch(setAiPlannerDraft(value));
              }}
              onSend={sendMessage}
              placeholder={
                showStarterView
                  ? "Search flights, hotels, destinations..."
                  : undefined
              }
              className={cn(
                "rounded-2xl border-border/70 bg-background shadow-md transition-shadow duration-200 focus-within:shadow-lg",
              )}
            />
            <p className="text-muted-foreground px-1 pt-2 text-center text-[11px]">
              AI can make mistakes. Verify critical travel details before
              booking.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
